/**
 * FollowUpForm.jsx
 *
 * Cartão inline para registar um follow-up no lead. Dispara o
 * endpoint ``POST /api/leads/{id}/scheduled-followups`` que:
 *   1. adiciona ao array ``scheduled_followups`` do lead;
 *   2. cria uma nota (``kind: 'followup'``) e sincroniza-a como Zoho Note
 *      no separador de notas do CRM (não só como Task);
 *   3. cria também a Task no Zoho (para a activity list do CRM).
 *
 * Fluxo UX:
 *   - Estado colapsado por defeito (mostra apenas botão "Registar
 *     follow-up") para não poluir o header.
 *   - Ao expandir mostra: data/hora (datetime-local), motivo
 *     (dropdown), nota (textarea).
 *   - Após submissão limpa os campos, colapsa, e chama ``onSaved``
 *     para o LeadDetail recarregar o timeline e a lista de notas.
 */
import { useState } from "react";
import { toast } from "sonner";
import { PhoneCall, Loader2, ChevronDown, ChevronUp, Send } from "lucide-react";
import api from "@/lib/api";

const REASON_OPTIONS = [
  "Cliente não atendeu",
  "Aguarda decisão familiar",
  "Enviar informação adicional",
  "Rever proposta / valores",
  "Confirmar visita técnica",
  "Retomar depois de férias",
  "Outro",
];

function _defaultDateTime() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function FollowUpForm({ leadId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(_defaultDateTime());
  const [reason, setReason] = useState(REASON_OPTIONS[0]);
  const [note, setNote] = useState("");

  const submit = async () => {
    if (!scheduledFor) { toast.error("Escolha data/hora do follow-up."); return; }
    if (!note.trim()) { toast.error("Escreva uma nota — vai sincronizar para o Zoho."); return; }
    setBusy(true);
    try {
      const iso = new Date(scheduledFor).toISOString();
      await api.post(`/leads/${leadId}/scheduled-followups`, {
        scheduled_for: iso,
        reason,
        note: note.trim(),
      });
      toast.success("Follow-up registado e nota sincronizada no Zoho.");
      setNote("");
      setScheduledFor(_defaultDateTime());
      setReason(REASON_OPTIONS[0]);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a registar follow-up");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        data-testid="btn-followup-open"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 font-semibold text-foreground"
        title="Registar próximo contacto — a nota vai para o Zoho"
      >
        <PhoneCall className="w-3.5 h-3.5" /> Registar follow-up
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <div data-testid="followup-form"
         className="w-full rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 border-b border-border">
        <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <PhoneCall className="w-4 h-4 text-muted-foreground" />
          Registar follow-up · nota sincroniza para o Zoho
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" title="Fechar">
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Próximo contacto (data/hora)
          </span>
          <input
            data-testid="followup-datetime"
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Motivo
          </span>
          <select
            data-testid="followup-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
          >
            {REASON_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="block text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Nota (obrigatória) — o texto vai para a secção Notas do Zoho
          </span>
          <textarea
            data-testid="followup-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex: falei com a filha, quer aguardar até 2ª feira para decidir com o irmão. Combinei ligar terça às 10h."
            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:border-slate-500"
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            {note.length} / 2000 caracteres
          </div>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2 px-4 py-2.5 bg-muted/40 border-t border-border">
        <button
          onClick={() => setOpen(false)}
          className="text-xs px-3 py-1.5 rounded-full bg-card border border-border text-muted-foreground hover:bg-muted"
        >
          Cancelar
        </button>
        <button
          data-testid="btn-followup-submit"
          onClick={submit}
          disabled={busy || !note.trim()}
          className="inline-flex items-center gap-2 text-xs px-4 py-1.5 rounded-full bg-foreground hover:bg-foreground/90 text-background font-semibold disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Registar & sincronizar Zoho
        </button>
      </div>
    </div>
  );
}
